import { jsPDF as JsPDF } from 'jspdf'
import computeNumbering from './compute-numbering'
import getSongFirstChord from './song-first-chord'

const PAGE_WIDTH = 210 // A4 mm
const PAGE_HEIGHT = 297
const MARGIN = 15
const USABLE_WIDTH = PAGE_WIDTH - MARGIN * 2
const USABLE_HEIGHT = PAGE_HEIGHT - MARGIN * 2
const TITLE_HEIGHT = 14 // mm reserved for header line (title + set number)

/**
 * Splits entries into "sets" divided by break items.
 * Sets are numbered sequentially (Set 1, Set 2, etc).
 */
function splitIntoSets (numbered) {
  const sets = []
  let current = { items: [] }

  numbered.forEach(({ entry, songNumber }) => {
    if (entry.type === 'note' && entry.isBreak) {
      if (current.items.length > 0) {
        sets.push(current)
      }
      current = { items: [] }
    } else {
      current.items.push({ entry, songNumber })
    }
  })

  if (current.items.length > 0) {
    sets.push(current)
  }

  return sets
}

/**
 * Builds text lines for a single set.
 * Returns array of { text, isNote } objects.
 */
function buildSetLines (items, columns) {
  const lines = []
  items.forEach(({ entry, songNumber }) => {
    if (entry.type === 'note') {
      lines.push({ text: `  [${entry.text}]`, isNote: true })
      return
    }
    let line = ''
    if (columns.number && songNumber) line += `${songNumber}. `
    if (columns.title) line += `${entry.title || ''}\t`
    if (columns.artist) line += `${entry.artist || ''}\t`
    if (columns.key) line += `${entry.key || ''}\t`
    if (columns.tempo) line += `${entry.tempo || ''}\t`
    if (columns.duration) line += `${entry.duration || ''}\t`
    if (columns.firstChord) line += `${getSongFirstChord(entry)}\t`
    if (columns.meta1) line += `${entry.meta1 || ''}\t`
    if (columns.meta2) line += `${entry.meta2 || ''}\t`
    if (columns.meta3) line += `${entry.meta3 || ''}\t`
    if (columns.meta4) line += `${entry.meta4 || ''}\t`
    lines.push({ text: line.replace(/\t+$/, '').replace(/\t/g, '   '), isNote: false })
  })
  return lines
}

/**
 * Calculates the largest font size that fits all lines on one page.
 * Reserves space for the title and set header at the top.
 */
function calcFontSize (doc, lines) {
  const availableHeight = USABLE_HEIGHT - TITLE_HEIGHT
  let fontSize = 28

  while (fontSize > 6) {
    doc.setFontSize(fontSize)
    const lineHeight = fontSize * 0.5
    const totalHeight = lines.length * lineHeight

    let widthOk = true
    for (const line of lines) {
      const textWidth = doc.getTextWidth(line.text)
      if (textWidth > USABLE_WIDTH) {
        widthOk = false
        break
      }
    }

    if (totalHeight <= availableHeight && widthOk) {
      return fontSize
    }
    fontSize -= 0.5
  }

  return 6
}

/**
 * Generates a PDF for a setlist and triggers download.
 *
 * @param {object} setlist - The setlist object with entries/songs
 * @param {object} columns - Which columns to include (matches clipboard options)
 */
export function generateSetlistPdf (setlist, columns) {
  const entries = getEntries(setlist)
  const numbered = computeNumbering(entries)
  const sets = splitIntoSets(numbered)
  const hasMultipleSets = sets.length > 1
  const title = setlist.title || 'Setlist'

  const doc = new JsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })

  sets.forEach((set, setIdx) => {
    if (setIdx > 0) {
      doc.addPage()
    }

    const lines = buildSetLines(set.items, columns)
    const fontSize = calcFontSize(doc, lines)
    const lineHeight = fontSize * 0.5

    let y = MARGIN

    // Header line: "Title — Set N" or just "Title" (bigger, bold, on one line)
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    const headerText = hasMultipleSets ? `${title} — Set ${setIdx + 1}` : title
    doc.text(headerText, PAGE_WIDTH / 2, y, { align: 'center' })
    y += TITLE_HEIGHT

    // Draw song/note lines
    doc.setFontSize(fontSize)
    lines.forEach((line) => {
      if (line.isNote) {
        doc.setFont('helvetica', 'italic')
      } else {
        doc.setFont('helvetica', 'normal')
      }
      doc.text(line.text, MARGIN, y)
      y += lineHeight
    })
  })

  // Empty setlist fallback
  if (sets.length === 0) {
    doc.setFontSize(18)
    doc.setFont('helvetica', 'bold')
    doc.text(title, PAGE_WIDTH / 2, MARGIN, { align: 'center' })
  }

  doc.save(`${title}.pdf`)
}

function getEntries (setlist) {
  if (setlist.entries && setlist.entries.length > 0) {
    return setlist.entries.map(e => {
      if (e.song) {
        const songId = e.song._id || e.song
        const songObj = (setlist.songs || []).find(s => (s._id || s) === songId)
        return songObj || { _id: songId }
      }
      return e
    })
  }
  return setlist.songs || []
}
